<script lang="ts">
  import type {
    Item,
    ItemActionClickEvent,
    ItemActionEvent,
    ItemProcessOptions,
    ProcessedItem,
    SourceWithCount,
  } from '../../modules/item/item.interface';
  import {
    Button,
    Dialog,
    List,
    ListItem,
    Subheader,
    Divider,
    Tooltip,
  } from 'svelte-materialify';
  import { querystring, location, push } from 'svelte-spa-router';
  import {
    CrawlerService,
    ItemService,
    ProjectConfigService,
    ReportService,
  } from '../../modules';
  import { alert, report, wrapProgress, day } from '../../store';
  import { ItemAction, ItemStatus } from '../../modules/item/item.constants';
  import { StatusCode, UiError } from '../../lib/errors';
  import Scroller from '../Scroller.svelte';
  import Page from '../Page.svelte';
  import ReportItemList from './ReportItemList.svelte';
  import ItemEdit from '../item/ItemEdit.svelte';
  import dayjs from 'dayjs';
  import type { ListQuery } from '../../types/listQuery';
  import { parseInt } from 'lodash';
  import { CategoryKey } from '../../modules/projects/project.interface';
  import ItemCompare from '../item/ItemCompare.svelte';

  export let params: { category: string };

  let category = params.category;
  let categoryConfig = getCategoryConfig();
  let editDialogActive = false;
  let itemOnEdit: Item;
  let oldItem: Item;
  let candidateSources: SourceWithCount[] = [];
  let processedItems: ProcessedItem[] = [];
  let queueItemCount: number;
  let isEdit = false;
  let statusAfterEdit = null;
  let visitSourceLink = null;
  let updatedAt: string;
  let sourceLastCrawled: string;
  let show = false;
  let editOldItemDialog = false;
  let fetchListItems: () => Promise<void>;

  $: onParamsChange(params);
  $: query = getQuery($querystring, category);

  fetchCounts();

  function onParamsChange(params: { category: string }) {
    if (category === params.category) return;

    category = params.category;
    categoryConfig = getCategoryConfig();
    fetchCounts();
  }

  function getCategoryConfig() {
    return ProjectConfigService.getCategoryConfig($report.project, params.category);
  }

  function getDefaultSortingForCategory() {
    if (!categoryConfig.candidateSortings?.length) {
      return 'createdAt';
    }

    return categoryConfig.candidateSortings[0];
  }

  function getQuery($queryString: string, category: string): ListQuery {
    const queryParams = new URLSearchParams($queryString);
    const hasDays = queryParams.get('days');

    const query: ListQuery = {
      source: queryParams.get('source'),
      status: (queryParams.get('status') as ItemStatus) || ItemStatus.REPORT,
      category,
      sort: queryParams.get('sort') || getDefaultSortingForCategory(),
    };
    if (hasDays) {
      query.days = parseInt(queryParams.get('days'));
    }

    return query;
  }

  function fetchCounts(_?: string) {
    fetchCandidateSources();
    fetchQueueItemCount();
    editOldItemDialog = false;
  }

  async function fetchCandidateSources() {
    candidateSources = await ItemService.getSourcesWithCandidateItemCounts(
      $report.project,
      params.category
    );

    if (query.status && query.status !== ItemStatus.REPORT) {
      return;
    }

    if (!$report.itemCounts[params.category] && candidateSources.length) {
      navigate(ItemStatus.CANDIDATE);
    }
  }

  async function fetchQueueItemCount() {
    const response = await ItemService.getItemCountOnQueue(
      $report.project,
      params.category
    );
    queueItemCount = response.count;
  }

  function createNewItem() {
    openItemEdit(ItemService.createDefaultItem($report.project, params.category), false);
  }

  function setProcessedItem(item: Item, processOptions: ItemProcessOptions) {
    let processedItem = processedItems.find((pi) => pi.id === item.id);

    if (processedItem) {
      processedItems = processedItems.filter((pi) => pi.id !== processedItem.id);
      processedItem = { ...processedItem, ...processOptions };
    } else {
      processedItem = { id: item.id, ...processOptions };
    }

    processedItems = [...processedItems, processedItem];
  }

  async function checkItemForReport({ project, url }) {
    oldItem = await ItemService.getOldReportItem(project, url).catch(() => null);
    return oldItem;
  }

  async function onItemActionClick(evt: ItemActionClickEvent) {
    const { action, item } = evt.detail;

    switch (action) {
      case ItemAction.EDIT:
        openItemEdit(item, true);
        break;
      case ItemAction.SELECT:
        if (
          item.category !== CategoryKey.BEHANCE &&
          item.category !== CategoryKey.DISCUSSION &&
          item.category !== CategoryKey.DRIBBBLE &&
          item.category !== CategoryKey.NFT &&
          item.category !== CategoryKey.INSTAGRAM
        ) {
          oldItem = await checkItemForReport(item);
          if (!oldItem) {
            changeStatus(item, ItemStatus.REPORT);
          } else {
            alert.show(
              `This link might be shared before ${item.url}`,
              'SHOW OLD LINK',
              () => openItemCompare(item, oldItem)
            );
          }
        } else {
          changeStatus(item, ItemStatus.REPORT);
        }
        break;
      case ItemAction.QUEUE:
        changeStatus(item, ItemStatus.QUEUE);
        break;
      case ItemAction.DISMISS:
        changeStatus(item, ItemStatus.DISMISSED);
        break;
      case ItemAction.UNDO:
        changeStatus(item, item.status, true);
        break;
      case ItemAction.REMOVE:
        removeItem(item);
    }
  }

  async function changeStatus(item: Item, status: ItemStatus, isUndo?: boolean) {
    const reportDate = status === ItemStatus.REPORT && $report.date;

    setProcessedItem(item, { loading: true });

    try {
      await ItemService.updateStatus(item.id, status, reportDate);
      fetchCounts();
      ReportService.getItemCounts($report.project, $report.date);
      const processedStatus = isUndo ? null : status;
      setProcessedItem(item, { status: processedStatus });
    } catch (error) {
      if (error instanceof UiError && error.code === StatusCode.BAD_REQUEST) {
        openItemEdit(item, true, status);
      }

      setProcessedItem(item, { status: null });

      throw error;
    } finally {
      setProcessedItem(item, { loading: false });
    }
  }

  async function removeItem(item: Item) {
    setProcessedItem(item, { loading: true });

    try {
      await ItemService.remove(item.id);
      setProcessedItem(item, { status: 'removed' });
    } catch (error) {
      alert.show('Problem removing item!');
    } finally {
      setProcessedItem(item, { loading: false });
    }
  }

  async function onItemCreate(evt: ItemActionEvent) {
    const { item, files } = evt.detail;

    if (item.status === ItemStatus.REPORT) {
      item.reportDate = $report.date;
    }

    const createdItem = await ItemService.create(item, files.get());
    fetchCounts();
    toggleDialog();
    fetchListItems();
    ReportService.getItemCounts($report.project, $report.date);

    if (item.status === ItemStatus.REPORT && createdItem.status === ItemStatus.QUEUE) {
      throw new UiError('Added item to Queue due to max count has reached!');
    }
  }

  async function onItemUpdate(evt: ItemActionEvent) {
    const { item, files } = evt.detail;

    await ItemService.update(item, files.get());

    if (statusAfterEdit) {
      await changeStatus(item, statusAfterEdit);
    }

    fetchCounts();
    fetchListItems();
    editDialogActive = false;
    editOldItemDialog = false;
  }

  function navigate(status: ItemStatus, source?: string) {
    let path = `${$location}?status=${status}`;
    if (
      query.category === CategoryKey.DRIBBBLE ||
      CategoryKey.BEHANCE ||
      CategoryKey.BRANDING
    ) {
      path += `&days=${$day}`;
    }
    if (source) {
      path += `&source=${source}`;
    }
    push(path);
  }

  function toggleDialog() {
    editDialogActive = !editDialogActive;
  }

  function toggleOldItemDialog() {
    editOldItemDialog = !editOldItemDialog;
  }

  function openItemEdit(item: Item, _isEdit: boolean, _statusAfterEdit?: ItemStatus) {
    itemOnEdit = item;
    isEdit = _isEdit;
    statusAfterEdit = _statusAfterEdit;
    toggleDialog();
  }

  function openItemCompare(item: Item, _oldItem: Item) {
    itemOnEdit = item;
    oldItem = _oldItem;
    toggleOldItemDialog();
  }

  async function crawlSource() {
    const crawlSourceRes = await CrawlerService.crawlSource(
      query.source === null ? category : query.source
    );

    if (crawlSourceRes.error) {
      throw new UiError(crawlSourceRes.error);
    }

    fetchCounts();
    fetchListItems();
    ReportService.getItemCounts($report.project, $report.date);
  }

  function getTotalCandidateCount(candidateSources: SourceWithCount[]) {
    return candidateSources.reduce((ttl, source) => (ttl += source.itemCount), 0);
  }

  function visitSource() {
    candidateSources.forEach((source) => {
      if (query.source === source.key) {
        visitSourceLink = source.url;
        window.open(visitSourceLink, '_blank').focus();
      }
    });
  }

  function getLastCrawled(date: string) {
    return dayjs(date).fromNow();
  }
</script>

<Page title={categoryConfig.name}>
  <div slot="actions">
    <div class="action-buttons">
      {#if query.source !== null && query.status === ItemStatus.CANDIDATE}
        <div class="tooltip">
          <Tooltip top bind:active={show}>
            <span>Last updated {updatedAt}</span>
            <span slot="tip">Last crawled {getLastCrawled(sourceLastCrawled)} </span>
          </Tooltip>
        </div>
        <Button on:click={visitSource}>Visit Source</Button>
      {/if}
      {#if query.status === ItemStatus.CANDIDATE && categoryConfig.key !== 'instagram' && query.source !== null}
        <Button class="ml-2" on:click={wrapProgress(crawlSource)}>Crawl Source</Button>
      {/if}
      <Button class="ml-2 green white-text" on:click={() => createNewItem()}
        >Create New</Button
      >
    </div>
  </div>
  <div slot="sidebar">
    <Scroller>
      <List class="elevation-2">
        <ListItem
          on:click={() => navigate(ItemStatus.REPORT)}
          active={query.status === ItemStatus.REPORT}
        >
          SELECTED
          <span slot="append">{$report.itemCounts[params.category] || 0}</span>
        </ListItem>
        <ListItem
          on:click={() => navigate(ItemStatus.QUEUE)}
          active={query.status === ItemStatus.QUEUE}
        >
          QUEUE
          <span slot="append">{queueItemCount ?? '...'}</span>
        </ListItem>
        {#if candidateSources.length}
          <Divider />
          <Subheader>Sources</Subheader>
          <ListItem
            on:click={() => navigate(ItemStatus.CANDIDATE, null)}
            active={query.status === ItemStatus.CANDIDATE && !query.source}
          >
            All
            <span slot="append">{getTotalCandidateCount(candidateSources)}</span>
          </ListItem>
          {#each candidateSources as source}
            <ListItem
              on:click={() => navigate(ItemStatus.CANDIDATE, source.key)}
              active={query.source === source.key}
            >
              {source.name}
              <span slot="append">{source.itemCount}</span>
            </ListItem>
          {/each}
        {/if}
      </List>
      {#if categoryConfig.cols && (($report.itemCounts[params.category] || 0) - categoryConfig.minCount) % categoryConfig.cols !== 0}
        <p class="text-caption mt-4">
          {#if categoryConfig.cols !== categoryConfig.minCount}
            <span
              >* Item count for {categoryConfig.name} must be match to formula: {categoryConfig.minCount}
              + {categoryConfig.cols}n</span
            >
          {:else}
            <span>
              * Item count for {categoryConfig.name} must be multiples of {categoryConfig.cols}
            </span>
          {/if}
        </p>
      {/if}
    </Scroller>
  </div>
  <ReportItemList
    bind:updatedAt
    bind:sourceLastCrawled
    {query}
    {processedItems}
    sources={candidateSources}
    bind:fetchListItems
    on:item-action-click={onItemActionClick}
  />
</Page>

<Dialog
  class="pa-2"
  width={params.category === 'product' ? `100%` : '1280px'}
  bind:active={editDialogActive}
>
  <ItemEdit
    bind:item={itemOnEdit}
    {categoryConfig}
    {isEdit}
    {statusAfterEdit}
    on:create={wrapProgress(onItemCreate)}
    on:update={wrapProgress(onItemUpdate)}
  />
</Dialog>

<Dialog class="pa-2" width="50%" bind:active={editOldItemDialog}>
  <ItemCompare
    bind:item={itemOnEdit}
    bind:oldItem
    {categoryConfig}
    on:fetch-items={() => fetchCounts()}
    on:fetch-list={() => fetchListItems()}
    on:update={wrapProgress(onItemUpdate)}
  />
</Dialog>

<style lang="scss">
  .action-buttons {
    display: flex;
    align-items: center;

    .tooltip {
      margin-right: 20px;
    }
  }
</style>
